import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import {
  TabContext,
  TabPanel,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator
} from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import CachedIcon from '@mui/icons-material/Cached';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import moment from 'moment';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import LocalPoliceOutlinedIcon from '@mui/icons-material/LocalPoliceOutlined';
import MarkResolvedModal from '../../components/_dashboard/smartlock/MarkResolvedModal';
import { ReactComponent as SevereAlertIcon } from '../../icons/severealerts.svg';
import { ReactComponent as ModerateAlertIcon } from '../../icons/moderatewarnings.svg';
import { ReactComponent as TripIcon } from '../../icons/tripIcon.svg';

import { messageService } from '../../MessagingService';
import { GetDashboardStatsData } from '../../network/Stats';
import { getNotificationEventStatus } from '../../utils/Utilities';

import { dateDiffInDays, isoDateFormatInDmyhs } from '../../utils/formatTime';
import useFeatures from '../../components/customContextProviders/FeatureContext';
import { URL_CONSTANT } from '../../URLConstants';

const useStyles = makeStyles({
  locationHistoryTitle: {
    fontSize: 14,
    fontWeight: 'bold'
  },

  locationHistoryAddress: {
    fontSize: 12
  },

  cardWithBorder: {
    background: '#FFFFFF',
    boxShadow: '0px 10px 19px 1px #EAEAFF',
    boxSizing: 'border-box',
    borderRadius: '20px',
    flexGrow: 1,
    overflow: 'auto',
    marginTop: 'auto',
    height: 'calc(100% - 60px)'
  }
});

export default function Notification({
  setDriverDetails,
  setAlertMetas,
  alertMetas,
  setIsDriverDetailModal
}) {
  const navigate = useNavigate();
  const classes = useStyles();
  const features = useFeatures();

  const [loading, setLoading] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const [severeAlertsData, setSevereAlertsData] = useState([]);
  const [resolvedEvent, setResolvedEvent] = useState(null);
  const [resolveEventError, setResolveEventError] = useState(false);
  const [moderateAlertsData, setModerateAlertsData] = useState([1, 2, 3]);
  const [tempSevereAlertData, setTempSevereAlertData] = useState([1, 2, 3]);
  const [openResolvedModal, setOpenResolvedModal] = useState(false);
  const [selectedAlertData, setSelectedAlertData] = useState(null);

  const handleSelectedTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleExpandChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    const subscription = messageService.onMessage().subscribe((message) => {
      if (message) {
        const msgPayload = JSON.parse(message?.text);
        if (msgPayload?.event === 'ON_ALERT') subscribitionAlerts(msgPayload);
      } else {
        // clear messages when empty message received
      }
    });
    getAllNotification();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  function subscribitionAlerts(msgPayload) {}

  const getAllNotification = async () => {
    try {
      setLoading(true);
      const res = await GetDashboardStatsData();
      setLoading(false);
      setSevereAlertsData(res?.data?.data?.notificationDetails || []);
    } catch (err) {
      setLoading(false);
      console.error('Error fetching notification data:', err);
    }
  };

  const handleNotificationResolved = () => {
    if (resolvedEvent === null) {
      setResolveEventError(true);
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: 'Please select notification to resolve'
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 60000);
    } else {
      setResolveEventError(false);
      setSelectedAlertData(resolvedEvent);
      setOpenResolvedModal(true);
    }
  };

  const driverDetailHandler = (data) => {
    setDriverDetails({
      name: data?.Trip?.driverName,
      phone: data?.Trip?.driverPhone,
      image: data?.Trip?.driverImage
    });
    setIsDriverDetailModal(true);
  };

  const handleRefresh = () => {
    getAllNotification();
  };

  const handleTripPage = (tripData) => {
    navigate(URL_CONSTANT.TRIP_DETAIL, { state: { tripData: tripData?.Trip } });
  };

  return (
    <>
      <Card
        data-testid="notification-section"
        id="notification_alerts_card"
        className={classes.cardWithBorder}
        sx={{ height: '100%' }}
      >
        <Box sx={{ mt: 2, overflowX: 'hidden' }}>
          <Stack
            direction="row"
            sx={{ p: 2 }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: '18px', lineHeight: '23px', color: '#171D40' }}
            >
              Notifications
            </Typography>
            {loading ? (
              <CircularProgress sx={{ color: '#000000' }} size={22} />
            ) : (
              <CachedIcon
                data-testid="refresh-notification-icon"
                onClick={handleRefresh}
                sx={{ color: '#000000', cursor: 'pointer' }}
              />
            )}
          </Stack>

          <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleSelectedTabChange}
                  variant="fullWidth"
                  aria-label="full width tabs example"
                >
                  <Tab wrapped label="Most recent" value="1" />
                  <Tab wrapped label="Yesterday" value="2" />
                  <Tab wrapped label="Day Before" value="3" />
                </Tabs>
              </Box>

              <TabPanel sx={{ p: 0, pt: 2 }} value="1">
                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some((event) => event?.severity === 'HIGH')
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Severe
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) => obj?.eventData?.filter((event) => event.severity === 'HIGH').length > 0
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{
                        mt: 2
                      }}
                      expanded={expanded === index}
                      onChange={handleExpandChange(index)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#C30A0A' }} />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ background: 'rgba(195, 10, 10, .1)', minHeight: '20px' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {TripIcon ? <TripIcon color="#C30A0A" /> : null}
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#C30A0A',
                              fontStyle: 'normal',
                              fontSize: '14px',
                              lineHeight: '14px',
                              textTransform: 'capitalize'
                            }}
                          >
                            {item?.Trip?.name} needs attention
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(195, 10, 10, .1)',
                          '& .MuiAccordionDetails-root': {
                            padding: '8px 10px 10px'
                          },
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'HIGH')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem
                                onClick={() => setResolvedEvent(event)}
                                sx={{
                                  cursor: 'pointer'
                                }}
                              >
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0
                                    }
                                  }}
                                >
                                  <TimelineDot
                                    color="primary"
                                    key={resolvedEvent?.id === event?.id ? 'filled' : 'outlined'}
                                    variant={
                                      resolvedEvent?.id === event?.id ? 'filled' : 'outlined'
                                    }
                                  />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}{' '}
                                    {event?.event_type?.toUpperCase() === 'LOCK_OPENED' &&
                                      `(${event?.channel})`}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {moment(parseInt(event?.timestamp, 10)).format(
                                      'DD-MM-YYYY hh:mm A'
                                    )}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}

                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Grid sx={{ textAlign: 'center' }} item xs={2.8}>
                            <Stack
                              direction="column"
                              justifyContent="center"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <CallOutlinedIcon sx={{ fontSize: 20 }} />
                              <Button
                                onClick={() => driverDetailHandler(item)}
                                sx={{ fontSize: 10, lineHeight: 'normal' }}
                                size="small"
                              >
                                Call
                              </Button>
                            </Stack>
                          </Grid>

                          <Divider orientation="vertical" flexItem />

                          <Grid sx={{ textAlign: 'center' }} item xs={2.8}>
                            <Stack
                              direction="column"
                              justifyContent="center"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <SupervisorAccountOutlinedIcon sx={{ fontSize: 20 }} />
                              <Button
                                onClick={() => handleTripPage(item)}
                                sx={{ fontSize: 10, lineHeight: 'normal' }}
                                size="small"
                              >
                                Details
                              </Button>
                            </Stack>
                          </Grid>
                          <Divider orientation="vertical" flexItem />
                          {features?.home?.markNotificationResolved && (
                            <>
                              <Grid sx={{ textAlign: 'center' }} item xs={3}>
                                <Stack
                                  direction="column"
                                  justifyContent="center"
                                  alignItems="center"
                                  spacing={0.5}
                                >
                                  <CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />
                                  <Button
                                    onClick={() => handleNotificationResolved()}
                                    sx={{ fontSize: 10, lineHeight: 'normal' }}
                                    size="small"
                                  >
                                    Mark Resolved
                                  </Button>
                                </Stack>
                              </Grid>
                              <Divider orientation="vertical" flexItem />
                            </>
                          )}

                          <Grid sx={{ textAlign: 'center' }} item xs={3}>
                            <Stack
                              direction="column"
                              justifyContent="center"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <LocalPoliceOutlinedIcon sx={{ fontSize: 20 }} />
                              <Button
                                onClick={() => handleTripPage(item)}
                                size="small"
                                sx={{ fontSize: 10, lineHeight: 'normal' }}
                              >
                                Police Stations
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some((event) => event?.severity === 'MEDIUM')
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      marginTop: '16px !important',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Moderate
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) =>
                      obj?.eventData?.length > 0 &&
                      obj?.eventData?.some((event) => event?.severity === 'MEDIUM')
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{ mt: 2 }}
                      expanded={expanded === item?.Trip?.id}
                      onChange={handleExpandChange(item?.Trip?.id)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ minHeight: '20px', bgcolor: 'rgba(255, 153, 0, .1)' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {ModerateAlertIcon ? <ModerateAlertIcon color="#FF9900" /> : null}
                          <Typography
                            data-testid="moderate-trip-button"
                            sx={{
                              fontWeight: 700,
                              fontSize: '14px',
                              lineHeight: '14px',
                              textTransform: 'capitalize'
                            }}
                          >
                            {item?.Trip?.name}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(255, 153, 0, .1)',
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'MEDIUM')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem>
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0 // Removes padding
                                    }
                                  }}
                                >
                                  <TimelineDot color="primary" />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {isoDateFormatInDmyhs(event?.createdAt)}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </TabPanel>

              <TabPanel sx={{ p: 0, pt: 2 }} value="2">
                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some(
                      (event) =>
                        event?.severity === 'HIGH' &&
                        dateDiffInDays(new Date(event?.updatedAt), new Date()) === 1
                    )
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Severe
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) =>
                      obj?.eventData?.length > 0 &&
                      obj?.eventData?.some(
                        (event) =>
                          event?.severity === 'HIGH' &&
                          dateDiffInDays(new Date(event?.updatedAt), new Date()) === 1
                      )
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{
                        mt: 2
                      }}
                      expanded={expanded === index}
                      onChange={handleExpandChange(index)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#C30A0A' }} />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ background: 'rgba(195, 10, 10, .1)', minHeight: '20px' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {SevereAlertIcon ? <SevereAlertIcon color="#C30A0A" /> : null}
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#C30A0A',
                              fontStyle: 'normal',
                              fontSize: '14px',
                              lineHeight: '14px',
                              textTransform: 'capitalize'
                            }}
                          >
                            {item?.Trip?.name} needs attention
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(195, 10, 10, .1)',
                          '& .MuiAccordionDetails-root': {
                            padding: '8px 10px 10px'
                          },
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'HIGH')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem>
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0 // Removes padding
                                    }
                                  }}
                                >
                                  <TimelineDot color="primary" />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {isoDateFormatInDmyhs(event?.createdAt)}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}

                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some(
                      (event) =>
                        event?.severity === 'MEDIUM' &&
                        dateDiffInDays(new Date(obj?.updatedAt), new Date()) === 1
                    )
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      marginTop: '16px !important',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Moderate
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) =>
                      obj?.eventData?.length > 0 &&
                      obj?.eventData?.some(
                        (event) =>
                          event?.severity === 'MEDIUM' &&
                          dateDiffInDays(new Date(event?.updatedAt), new Date()) === 1
                      )
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{ mt: 2 }}
                      expanded={expanded === item?.Trip?.id}
                      onChange={handleExpandChange(item?.Trip?.id)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ minHeight: '20px', bgcolor: 'rgba(255, 153, 0, .1)' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {ModerateAlertIcon ? <ModerateAlertIcon color="#FF9900" /> : null}
                          <Typography
                            sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '14px' }}
                          >
                            {item?.Trip?.name}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(255, 153, 0, .1)',
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'MEDIUM')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem>
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0 // Removes padding
                                    }
                                  }}
                                >
                                  <TimelineDot color="primary" />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {isoDateFormatInDmyhs(event?.createdAt)}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </TabPanel>

              <TabPanel sx={{ p: 0, pt: 2 }} value="3">
                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some(
                      (event) =>
                        event?.severity === 'HIGH' &&
                        dateDiffInDays(new Date(event?.updatedAt), new Date()) >= 2
                    )
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      mt: 2,
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Severe
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) =>
                      obj?.eventData?.length > 0 &&
                      obj?.eventData?.some(
                        (event) =>
                          event?.severity === 'HIGH' &&
                          dateDiffInDays(new Date(event?.updatedAt), new Date()) >= 2
                      )
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{
                        mt: 2
                      }}
                      expanded={expanded === index}
                      onChange={handleExpandChange(index)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#C30A0A' }} />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ background: 'rgba(195, 10, 10, .1)', minHeight: '20px' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {SevereAlertIcon ? <SevereAlertIcon color="#C30A0A" /> : null}
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: '#C30A0A',
                              fontStyle: 'normal',
                              fontSize: '14px',
                              lineHeight: '14px',
                              textTransform: 'capitalize'
                            }}
                          >
                            {item?.Trip?.name} needs attention
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(195, 10, 10, .1)',
                          '& .MuiAccordionDetails-root': {
                            padding: '8px 10px 10px'
                          },
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'HIGH')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem>
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0 // Removes padding
                                    }
                                  }}
                                >
                                  <TimelineDot color="primary" />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {isoDateFormatInDmyhs(event?.createdAt)}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}

                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some((event) => event?.severity === 'MEDIUM')
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      mt: 2,
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Moderate
                  </Typography>
                ) : null}

                {severeAlertsData?.filter(
                  (obj) =>
                    obj?.eventData?.length > 0 &&
                    obj?.eventData?.some(
                      (event) =>
                        event?.severity === 'MEDIUM' &&
                        dateDiffInDays(new Date(obj?.updatedAt), new Date()) >= 2
                    )
                )?.length > 0 ? (
                  <Typography
                    sx={{
                      pl: 3,
                      mt: 2,
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#535353'
                    }}
                  >
                    Moderate
                  </Typography>
                ) : null}

                {severeAlertsData
                  ?.filter(
                    (obj) =>
                      obj?.eventData?.length > 0 &&
                      obj?.eventData?.some(
                        (event) =>
                          event?.severity === 'MEDIUM' &&
                          dateDiffInDays(new Date(event?.updatedAt), new Date()) >= 2
                      )
                  )
                  ?.map((item, index) => (
                    <Accordion
                      key={index}
                      sx={{ mt: 2 }}
                      expanded={expanded === item?.Trip?.id}
                      onChange={handleExpandChange(item?.Trip?.id)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                        sx={{ minHeight: '20px', bgcolor: 'rgba(255, 153, 0, .1)' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {ModerateAlertIcon ? <ModerateAlertIcon color="#FF9900" /> : null}
                          <Typography
                            sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '14px' }}
                          >
                            {item?.Trip?.name}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          bgcolor: 'rgba(255, 153, 0, .1)',
                          maxHeight: '40vh',
                          overflowY: 'scroll',
                          overflowX: 'hidden'
                        }}
                      >
                        {item?.eventData
                          ?.filter((obj) => obj?.severity === 'MEDIUM')
                          ?.map((event, index, arr) => (
                            <Timeline
                              key={index}
                              sx={{
                                alignItems: 'flex-start',
                                '& .MuiTimelineItem-root:before': {
                                  padding: '0 !important'
                                },
                                '& .MuiTimelineItem-root': {
                                  minHeight: '40px'
                                },
                                '& .MuiTimelineDot-root': {
                                  padding: '2px'
                                }
                              }}
                            >
                              <TimelineItem>
                                <TimelineSeparator
                                  sx={{
                                    '& .MuiTimelineItem-root:before': {
                                      padding: 0 // Removes padding
                                    }
                                  }}
                                >
                                  <TimelineDot color="primary" />
                                  {index !== (arr?.length ?? 0) - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ width: '100%', padding: '6px 10px' }}>
                                  <Typography sx={{ fontSize: 14 }}>
                                    {' '}
                                    {getNotificationEventStatus(event?.event_type)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12 }}>
                                    {event?.device_name}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, width: '100vw' }}>
                                    {' '}
                                    {isoDateFormatInDmyhs(event?.createdAt)}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </Timeline>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Card>
      <MarkResolvedModal
        openAssignedModal={openResolvedModal}
        handleCloseModal={() => setOpenResolvedModal(false)}
        setAlertMetas={setAlertMetas}
        alertMetas={alertMetas}
        selectedAlert={selectedAlertData}
        updatedNotification={handleRefresh}
      />
    </>
  );
}
